import { Common, dataServices, Promise, Realty, services } from '@ambita/ambita-components-core';
import { ApiOwnership, ApiUnregisteredOwnership, NeighborCadastreOwner } from '../typings/Owner';

const { filters, util } = services;

export interface MappedAddress {
  city: string;
  letter?: string;
  postcode: string;
  title: string;
}

export interface OwnershipData extends ApiOwnership {
  owningCadastreIdent?: Realty.CadastreIdent;
  dwellingType: Common.CodeText;
  useOfCadastre: Common.CodeText;
  links: {
    ownedCadastre: Common.DefaultLink;
    conveyance: Common.DefaultLink;
    owningPerson: Common.DefaultLink;
    self: Common.DefaultLink;
  };
  registered: boolean;
}

export interface UnregisteredOwnershipData extends ApiUnregisteredOwnership {
  links: {
    ownedCadastre: Common.DefaultLink;
    owningPerson: Common.DefaultLink;
    self: Common.DefaultLink;
  };
}

export interface Cadastre extends Realty.ApiCadastre {
  addresses: Realty.ApiAddress[];
  owners: NeighborCadastreOwner[];
  ownershipsOwningThisCadastre: OwnershipData[];
  unregisteredOwnershipsOwningThisCadastre: UnregisteredOwnershipData[];
}

export function createEmptyAddress() {
  return {
    title: '',
    postcode: '',
    city: ''
  };
}

function transformRealtyResponse(response: Realty.ApiResponse) {
  if (response.item) {
    return [response.item];
  }

  return response.items;
}

export function fetchCadastres(cadastreNumber: string): Promise<Realty.ApiCadastre[]> {
  return dataServices.realty.getCadastres(cadastreNumber.replace(/\//g, '-'))
    .then(transformRealtyResponse);
}

export function fetchPersons(personKey: string): Promise<Realty.ApiPerson[]> {
  return dataServices.realty.getPersons(personKey)
    .then(transformRealtyResponse);
}

export function fetchCadastreDetails(cadastre: Realty.ApiCadastre): Promise<Cadastre> {
  const queryParameters = {
    pagesize: 1000
  };
  const cadastreDetailsPromise: any = [
    fetchResourcesByLink(cadastre.links.addresses.href),
    fetchResourcesByLink(cadastre.links.owners.href, queryParameters),
    fetchResourcesByLink(cadastre.links.ownershipsOwningThisCadastre.href, queryParameters),
    fetchResourcesByLink(cadastre.links.unregisteredOwnershipsOwningThisCadastre.href, queryParameters)
  ];

  const isBaseCadastre = cadastre.ident.leaseholdUnitNumber === 0 && cadastre.ident.sectionNumber === 0;

  if (!isBaseCadastre) {
    const cadastreIdent = cadastre.ident;
    const baseCadastre =
      `${cadastreIdent.municipalityNumber}-${cadastreIdent.cadastralUnitNumber}-${cadastreIdent.unitNumber}-0-0`;

    cadastreDetailsPromise.push(
      dataServices.realty.getCadastreOwnershipOwningThisCadastre(baseCadastre, queryParameters)
        .then(transformRealtyResponse)
    );
  }

  return Promise.all(cadastreDetailsPromise).then((detailsResponse: { [key: string]: any }) => {
    const addresses = detailsResponse[0];
    const owners = detailsResponse[1];
    let ownershipsOwningThisCadastre = detailsResponse[2];
    const unregisteredOwnershipsOwningThisCadastre = detailsResponse[3];
    const ownershipsOwningBaseCadastre = detailsResponse[4];

    if (ownershipsOwningThisCadastre && ownershipsOwningBaseCadastre) {
      ownershipsOwningThisCadastre = [...ownershipsOwningThisCadastre, ...ownershipsOwningBaseCadastre];
    }

    return util.assign({}, cadastre, {
      addresses,
      owners,
      ownershipsOwningThisCadastre,
      unregisteredOwnershipsOwningThisCadastre
    });
  });
}

export function fetchResourcesByLink(url: string, options?: any): Promise<any> {
  return dataServices.realty.getResourceByLink(url, options).then(transformRealtyResponse);
}

export function fetchCadastreOwningCadastre(cadastreKey: string): Promise<any> {
  const NON_NUMERIC_REGEX = /\W+/g;
  const cleanedCadastreKey = cadastreKey.replace(NON_NUMERIC_REGEX, '-');
  const queryParameters = {
    pagesize: 1000
  };

  return dataServices.realty.getCadastreOwnershipOwningThisCadastre(cleanedCadastreKey, queryParameters)
    .then(transformRealtyResponse);
}

export function mapCadastreIdentity(cadastreIdent: Realty.CadastreIdent): Realty.MappedCadastreIdentity {
  return {
    knr: cadastreIdent.municipalityNumber,
    gnr: cadastreIdent.cadastralUnitNumber,
    bnr: cadastreIdent.unitNumber,
    fnr: cadastreIdent.leaseholdUnitNumber,
    snr: cadastreIdent.sectionNumber
  };
}

export function findSelectedAddress(addressId: string, addresses: Realty.ApiAddress[]): Realty.ApiAddress {
  if (!addressId) {
    return;
  }

  for (const address of addresses) {
    if (address.key === addressId) {
      return address;
    }
  }
}

export function mapAddress(address: Realty.ApiAddress): MappedAddress {
  if (!address) {
    return createEmptyAddress();
  }

  const { postalCode, postalPlace } = address.street || address.postalPlace;
  const code = filters.padLeft(postalCode, 4, '0');
  const city = postalPlace;
  const addressNumber = address.ident.addressNumber || '';
  const letter = address.ident.letter || '';
  const streetName = address.street ? address.street.streetName : '';
  const title = `${streetName} ${addressNumber}${letter}`.trim();
  const altTitle = `${code} ${city}`.trim();
  const addressTitle = title === '' ? altTitle : title;

  return {
    title: addressTitle,
    postcode: code,
    city,
    letter
  };
}

export function getOrganizationAddress(organization: Realty.ApiPerson): MappedAddress {
  const address = organization.postalAddress || organization.businessAddress;

  if (!address) {
    return createEmptyAddress();
  }

  const addressParts = address.split(',');
  const zipCity = addressParts.length > 1 ? addressParts[1].trim().split(' ') : ['0', ' '];

  return {
    title: addressParts[0],
    postcode: zipCity[0],
    city: zipCity[1]
  };
}
